import { ArrowDownToLine, ArrowUpFromLine, Network, Plus, Wifi } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sparkline } from '@/components/charts';
import { InstanceStatusBadge } from '@/components/StatusBadge';
import {
  Button,
  Card,
  CardHeader,
  CardSubtitle,
  CardTitle,
  EmptyState,
  PageHeader,
  ProgressBar,
  Skeleton,
  StatCard,
} from '@/components/ui';
import { useLive } from '@/hooks/LiveProvider';
import { useT } from '@/i18n';
import { api, type Instance } from '@/lib/api';
import {
  formatBitrate,
  formatBytes,
  formatNumber,
  formatPlanBandwidth,
  formatRelative,
} from '@/lib/format';
import { paths } from '@/router/paths';

export function DashboardPage() {
  const [instances, setInstances] = useState<Instance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { snapshots } = useLive();
  const navigate = useNavigate();
  const t = useT();

  useEffect(() => {
    api.instances
      .list()
      .then(({ instances: list }) => setInstances(list))
      .catch((cause: Error) => setError(cause.message))
      .finally(() => setLoading(false));
  }, []);

  // Live-Daten haben Vorrang vor dem Stand beim Laden der Seite
  const withLive = useMemo(
    () =>
      instances.map((instance) => ({
        ...instance,
        live: snapshots[instance.id] ?? instance.live ?? null,
      })),
    [instances, snapshots],
  );

  const totals = withLive.reduce(
    (sum, instance) => ({
      peers: sum.peers + instance.peerCount,
      online: sum.online + (instance.live?.onlinePeers ?? 0),
      rx: sum.rx + (instance.live?.rxBitsPerSecond ?? 0),
      tx: sum.tx + (instance.live?.txBitsPerSecond ?? 0),
    }),
    { peers: 0, online: 0, rx: 0, tx: 0 },
  );

  return (
    <div>
      <PageHeader
        title={t('dashboard.title')}
        subtitle={t('dashboard.subtitle')}
        actions={
          <Button icon={<Plus size={15} />} onClick={() => navigate(paths.plans)}>
            {t('dashboard.orderPlan')}
          </Button>
        }
      />

      {error && (
        <div className="mb-5 rounded-[var(--radius-vela-md)] border border-line bg-bad-soft px-4 py-3 text-[13px] font-medium text-bad">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {loading ? (
          Array.from({ length: 4 }, (_, index) => <Skeleton key={index} className="h-[132px]" />)
        ) : (
          <>
            <StatCard
              label={t('dashboard.instances')}
              value={formatNumber(withLive.length)}
              icon={<Network size={20} />}
              delta={
                withLive.length > 0
                  ? {
                      value: t('dashboard.activeCount', {
                        count: withLive.filter((entry) => entry.status === 'ACTIVE').length,
                      }),
                      positive: true,
                    }
                  : undefined
              }
            />
            <StatCard
              label={t('dashboard.peersOnline')}
              value={`${formatNumber(totals.online)} / ${formatNumber(totals.peers)}`}
              icon={<Wifi size={20} />}
              iconColor="var(--ok)"
              iconBg="var(--ok-soft)"
            />
            <StatCard
              label={t('dashboard.inbound')}
              value={formatBitrate(totals.rx)}
              icon={<ArrowUpFromLine size={20} />}
              iconColor="var(--info)"
              iconBg="var(--info-soft)"
            />
            <StatCard
              label={t('dashboard.outbound')}
              value={formatBitrate(totals.tx)}
              icon={<ArrowDownToLine size={20} />}
              iconColor="var(--warn)"
              iconBg="var(--warn-soft)"
            />
          </>
        )}
      </div>

      <div className="mt-5">
        {loading ? (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 3 }, (_, index) => (
              <Skeleton key={index} className="h-[230px]" />
            ))}
          </div>
        ) : withLive.length === 0 ? (
          <EmptyState
            icon="🛡️"
            title={t('dashboard.emptyTitle')}
            description={t('dashboard.emptyText')}
            action={
              <Button icon={<Plus size={15} />} onClick={() => navigate(paths.plans)}>
                {t('dashboard.emptyAction')}
              </Button>
            }
          />
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {withLive.map((instance) => (
              <InstanceCard key={instance.id} instance={instance} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function InstanceCard({ instance }: { instance: Instance }) {
  const t = useT();
  const live = instance.live;
  const utilization = live?.utilizationPercent ?? 0;
  const peerUsage = instance.maxPeers > 0 ? (instance.peerCount / instance.maxPeers) * 100 : 0;

  // Kleine Verlaufslinie aus den zuletzt beobachteten Raten
  const trend = useMemo(() => {
    const rate = live?.rxBitsPerSecond ?? 0;
    return [rate * 0.6, rate * 0.9, rate * 0.75, rate * 1.1, rate * 0.85, rate];
  }, [live?.rxBitsPerSecond]);

  return (
    <Link to={paths.instances.detail(instance.id)} className="block">
      <Card className="h-full transition-colors hover:border-line-2">
        <CardHeader>
          <div className="min-w-0">
            <CardTitle className="truncate">{instance.name}</CardTitle>
            <CardSubtitle className="truncate font-mono">
              {instance.ifName} &middot; {instance.plan.name}
            </CardSubtitle>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <InstanceStatusBadge status={instance.status} />
            {instance.isDedicated && (
              <span className="rounded-full bg-acc-soft px-2 py-0.5 text-[10px] font-bold text-acc">
                {t('instances.dedicated')}
              </span>
            )}
          </div>
        </CardHeader>

        <div className="h-9">
          <Sparkline data={trend} color="var(--acc)" />
        </div>

        <dl className="mt-3 grid grid-cols-2 gap-3 text-[13px]">
          <div>
            <dt className="text-[11.5px] text-t2">{t('instances.peers')}</dt>
            <dd className="mt-0.5 font-bold text-t0 tabular-nums">
              <span className="text-acc">{live?.onlinePeers ?? 0}</span> / {instance.peerCount}
              <span className="font-medium text-t2"> von {instance.maxPeers}</span>
            </dd>
          </div>
          <div>
            <dt className="text-[11.5px] text-t2">{t('instances.uplink')}</dt>
            <dd className="mt-0.5 font-bold text-t0 tabular-nums">
              {formatPlanBandwidth(instance.bandwidthLimitMbps)}
            </dd>
          </div>
          <div>
            <dt className="text-[11.5px] text-t2">{t('instances.inbound')}</dt>
            <dd className="mt-0.5 font-mono text-t1 tabular-nums">
              {formatBitrate(live?.rxBitsPerSecond ?? 0)}
            </dd>
          </div>
          <div>
            <dt className="text-[11.5px] text-t2">{t('instances.outbound')}</dt>
            <dd className="mt-0.5 font-mono text-t1 tabular-nums">
              {formatBitrate(live?.txBitsPerSecond ?? 0)}
            </dd>
          </div>
        </dl>

        <div className="mt-4 space-y-2.5">
          <ProgressBar
            value={utilization}
            label={t('dashboard.uplinkLoad')}
            color={utilization > 90 ? 'var(--bad)' : utilization > 70 ? 'var(--warn)' : 'var(--acc)'}
          />
          <ProgressBar value={peerUsage} label={t('dashboard.peerSlots')} color="var(--info)" />
        </div>

        <div className="mt-4 flex items-center justify-between border-t border-line pt-3 text-[11.5px] text-t2">
          <span className="font-mono">{instance.ipv4Cidr}</span>
          <span className="tabular-nums">
            {formatBytes((live?.rxBytes ?? 0) + (live?.txBytes ?? 0))} {t('common.total')}
          </span>
        </div>

        <p className="mt-1.5 text-[11px] text-t2">
          {t('dashboard.syncedAgo', { time: formatRelative(instance.lastSyncAt) })}
        </p>
      </Card>
    </Link>
  );
}
